import { Helmet } from 'react-helmet-async'

const StructuredData = () => {
  // Organization Schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Mocksy',
    description:
      'AI-powered product photo transformation service for e-commerce and marketing professionals',
    url: 'https://getmocksy.com',
    logo: 'https://getmocksy.com/logo.png',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'legal@getmocksy.com',
      contactType: 'Customer Service',
    },
    sameAs: [],
  }

  // WebSite Schema with Search Action
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Mocksy',
    url: 'https://getmocksy.com',
    description: 'Transform your product photos with AI in seconds',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://getmocksy.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  }

  // SoftwareApplication Schema (Product)
  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Mocksy',
    applicationCategory: 'DesignApplication',
    operatingSystem: 'Web Browser',
    description:
      'AI-powered product photo transformation tool. Upload your product image, describe the desired changes, and generate professional variations instantly.',
    offers: {
      '@type': 'Offer',
      price: '50.00',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '50.00',
        priceCurrency: 'USD',
        billingDuration: 'P1M',
        billingIncrement: 1,
      },
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: 'Mocksy',
      },
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '127',
    },
    featureList: [
      'AI-powered image generation',
      'Unlimited image variations',
      'Professional quality output',
      'Fast processing (seconds)',
      'Secure cloud storage',
      'Download in high resolution',
      'Easy-to-use interface',
      'No technical skills required',
    ],
  }

  // FAQ Schema - will be populated by FAQSection component
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is Mocksy?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Mocksy is an AI-powered platform that transforms your product photos into professional variations. Simply upload an image, describe the changes you want, and our AI generates high-quality product photography in seconds.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does AI image generation work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Mocksy uses advanced AI models to understand your product image and generate variations based on your text description. The AI analyzes your original photo, applies the requested transformations, and creates professional-quality results while maintaining product integrity.',
        },
      },
      {
        '@type': 'Question',
        name: 'What file formats are supported?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Mocksy supports common image formats including JPG, JPEG, PNG, and WEBP. Images are processed and can be downloaded in high-resolution PNG format for maximum quality.',
        },
      },
      {
        '@type': 'Question',
        name: 'How long does image generation take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Image generation typically takes just seconds. Our AI processes your request and generates multiple high-quality variations almost instantly, allowing you to iterate quickly and find the perfect result.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is my data secure?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, absolutely. All images are processed securely using encrypted connections. Your uploaded photos and generated variations are stored safely in the cloud and are only accessible to you. We take data privacy and security seriously.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I cancel my subscription anytime?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, you can cancel your subscription at any time. Cancellations take effect at the end of the current billing period, and you retain access to all features until then.',
        },
      },
      {
        '@type': 'Question',
        name: 'What makes Mocksy different from other photo editing tools?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Mocksy uses AI to understand context and generate professional product photos automatically. Unlike traditional photo editors that require manual editing skills, Mocksy works through simple text descriptions. Just tell it what you want, and the AI handles the complex transformations.',
        },
      },
    ],
  }

  // BreadcrumbList Schema (for homepage)
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://getmocksy.com',
      },
    ],
  }

  return (
    <Helmet>
      {/* Organization Schema */}
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>

      {/* WebSite Schema */}
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>

      {/* Software Application Schema */}
      <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>

      {/* FAQ Schema */}
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Breadcrumb Schema */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
    </Helmet>
  )
}

export default StructuredData
